// client/src/components/event/TaskComments.jsx
//
// The comment thread for a single task. Reuses the server-side per-task
// message thread (t_message_thread, context_type='task'). No live socket on
// the client — we poll every 20s while mounted and refetch after posting,
// matching how the rest of the messaging UI stays fresh.

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaPaperclip, FaPaperPlane, FaFileAlt, FaTimes } from "react-icons/fa";
import { getSessionLocal, sessionRequestConfig } from "../../services/sessionLocal";
import CommentBox from "./CommentBox";
import "./TaskComments.css";

const baseUrl = process.env.REACT_APP_BASE_URL || "";
const POLL_MS = 20000;
const IMG_RE = /^image\//i;

function formatWhen(value) {
  if (!value) return "";
  try {
    const d = new Date(String(value).includes("Z") || String(value).includes("T") ? value : value.replace(" ", "T") + "Z");
    if (Number.isNaN(d.getTime())) return String(value);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(value);
  }
}

function renderText(text) {
  // Bold @mentions; leave the rest as plain text.
  const parts = String(text || "").split(/(@[a-zA-Z][a-zA-Z0-9_.-]*)/g);
  return parts.map((p, i) =>
    p.startsWith("@")
      ? <strong key={i} className="taskcomments-mention">{p}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

export default function TaskComments({ tenant, eventId, taskId, taskTitle }) {
  const session = getSessionLocal();
  const myId = session.userId;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchComments = useCallback(async () => {
    if (!tenant || !eventId || !taskId) return;
    try {
      const cfg = sessionRequestConfig();
      const { data } = await axios.get(
        `${baseUrl}/api/events/${tenant}/${eventId}/tasks/${taskId}/comments`,
        cfg
      );
      if (data?.success) {
        setComments(data.comments || []);
        setError(null);
      }
    } catch (err) {
      console.error("Error loading task comments:", err);
      setError("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [tenant, eventId, taskId]);

  useEffect(() => {
    setLoading(true);
    fetchComments();
    const id = setInterval(fetchComments, POLL_MS);
    return () => clearInterval(id);
  }, [fetchComments]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments.length]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles((prev) => [...prev, ...files].slice(0, 8));
    e.target.value = "";
  };

  const removePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const send = async () => {
    const body = text.trim();
    if (!body && pendingFiles.length === 0) return;
    setSending(true);
    setError(null);
    try {
      const cfg = sessionRequestConfig();
      let fileIds = [];

      if (pendingFiles.length) {
        const fd = new FormData();
        pendingFiles.forEach((f) => fd.append("files", f));
        const upRes = await axios.post(
          `${baseUrl}/api/events/${tenant}/${eventId}/tasks/${taskId}/attachments`,
          fd,
          { ...cfg, headers: { ...cfg.headers, "Content-Type": "multipart/form-data" } }
        );
        fileIds = (upRes.data?.attachments || []).map((a) => a.id);
      }

      await axios.post(
        `${baseUrl}/api/events/${tenant}/${eventId}/tasks/${taskId}/comments`,
        { text: body, fileIds, createdBy: session.username },
        cfg
      );

      setText("");
      setPendingFiles([]);
      await fetchComments();
      window.dispatchEvent(new Event("new-message"));
      window.dispatchEvent(new Event("unread:refresh"));
    } catch (err) {
      console.error("Error posting comment:", err);
      setError(err.response?.data?.message || "Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="taskcomments">
      <div className="taskcomments-list" ref={listRef}>
        {loading && <div className="taskcomments-empty">Loading…</div>}
        {!loading && error && <div className="taskcomments-error">{error}</div>}
        {!loading && !error && comments.length === 0 && (
          <div className="taskcomments-empty">No comments yet. Start the discussion below.</div>
        )}
        {comments.map((c) => {
          const mine = Number(c.senderId) === Number(myId);
          return (
            <div key={c.id} className={`taskcomments-item ${mine ? "is-mine" : ""}`}>
              <div className="taskcomments-meta">
                <span className="taskcomments-author">{mine ? "You" : c.senderName || c.senderUsername}</span>
                <span className="taskcomments-time">{formatWhen(c.createdAt)}</span>
              </div>
              {c.text && c.text !== "(attachment)" && (
                <div className="taskcomments-text">{renderText(c.text)}</div>
              )}
              {c.attachments?.length > 0 && (
                <div className="taskcomments-attachments">
                  {c.attachments.map((a) =>
                    IMG_RE.test(a.type) ? (
                      <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
                        <img src={a.url} alt="attachment" className="taskcomments-thumb" />
                      </a>
                    ) : (
                      <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="taskcomments-file">
                        <FaFileAlt /> {a.path?.split("/").pop() || "file"}
                      </a>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pendingFiles.length > 0 && (
        <div className="taskcomments-pending">
          {pendingFiles.map((f, i) => (
            <span key={i} className="taskcomments-pending-chip">
              {f.name}
              <button type="button" onClick={() => removePending(i)} aria-label="Remove">
                <FaTimes />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="taskcomments-composer">
        <CommentBox
          value={text}
          onChange={(e) => setText(e?.target?.value ?? e)}
          onCommentChange={setText}
          placeholder="Add a comment… use @ to mention"
          rows={2}
        />
        <div className="taskcomments-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={onPickFiles}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title="Attach files"
          >
            <FaPaperclip />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={send}
            disabled={sending || (!text.trim() && pendingFiles.length === 0)}
          >
            <FaPaperPlane /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
